import { defineMessages, IntlShape, MessageDescriptor } from 'react-intl'

import { ConsentBannerStatus, ConsentStatus, MIXED_CONSENT_BANNER_STATUS } from '../../models/consent/Consent'
import { getPossessivePronoun } from '../common/utils/userUtils'

/** Who a provider-facing consent banner is asking to sign. Caregiver pronouns aren't collected, so "their" is used. */
export type ConsentBannerRecipients = {
  client?: { firstName?: string; pronouns?: string[] }
  caregiver?: { firstName?: string }
}

export const consentBannerMessages = defineMessages({
  expiringSoon: {
    defaultMessage:
      'Please ask {clientFirstName} to sign {possessivePronoun} new annual Informed Consent form. Clients must sign each year, and their previous form will expire soon.',
    description: 'Banner text informing provider that client consent is expiring soon',
  },
  expired: {
    defaultMessage:
      'Please ask {clientFirstName} to sign {possessivePronoun} new annual Informed Consent form. {capitalPossessivePronoun} previous form has expired, and clients must sign each year.',
    description: 'Banner text informing provider that client consent has expired',
  },
  superseded: {
    defaultMessage:
      'Please ask {clientFirstName} to sign {possessivePronoun} updated Informed Consent form. Changes from the previously signed version have been made and require review.',
    description: 'Banner text informing provider that a new consent version requires signing',
  },
  pending: {
    defaultMessage: 'Please ask {clientFirstName} to sign {possessivePronoun} Informed Consent form.',
    description: 'Banner text informing provider that client consent is pending signature',
  },
  caregiverExpiringSoon: {
    defaultMessage:
      'Please ask {caregiverFirstName} to sign their new annual Informed Consent form. Individuals must sign each year, and their previous form will expire soon.',
    description: 'Banner text informing provider that a caregiver consent is expiring soon',
  },
  caregiverExpired: {
    defaultMessage:
      'Please ask {caregiverFirstName} to sign their new annual Informed Consent form. Their previous form has expired, and individuals must sign each year.',
    description: 'Banner text informing provider that a caregiver consent has expired',
  },
  caregiverSuperseded: {
    defaultMessage:
      'Please ask {caregiverFirstName} to sign their updated Informed Consent form. Changes from the previously signed version have been made and require review.',
    description: 'Banner text informing provider that a new consent version requires the caregiver to sign',
  },
  caregiverPending: {
    defaultMessage: 'Please ask {caregiverFirstName} to sign their Informed Consent form.',
    description: 'Banner text informing provider that a caregiver consent is pending signature',
  },
  clientAndCaregiverExpiringSoon: {
    defaultMessage:
      'Please ask {clientFirstName} and {caregiverFirstName} to sign their new annual Informed Consent forms. Individuals must sign each year, and their previous forms will expire soon.',
    description: 'Banner text informing provider that both the client and caregiver consents are expiring soon',
  },
  clientAndCaregiverExpired: {
    defaultMessage:
      'Please ask {clientFirstName} and {caregiverFirstName} to sign their new annual Informed Consent forms. Their previous forms have expired, and individuals must sign each year.',
    description: 'Banner text informing provider that both the client and caregiver consents have expired',
  },
  clientAndCaregiverSuperseded: {
    defaultMessage:
      'Please ask {clientFirstName} and {caregiverFirstName} to sign their updated Informed Consent forms. Changes from the previously signed versions have been made and require review.',
    description:
      'Banner text informing provider that a new consent version requires both the client and caregiver to sign',
  },
  clientAndCaregiverPending: {
    defaultMessage: 'Please ask {clientFirstName} and {caregiverFirstName} to sign their Informed Consent forms.',
    description: 'Banner text informing provider that both the client and caregiver consents are pending signature',
  },
  clientAndCaregiverMixed: {
    defaultMessage:
      'Please ask {clientFirstName} and/or {caregiverFirstName} to sign their Informed Consent forms. Individuals must sign each year, and their previous forms have expired, will expire soon, or have changed.',
    description:
      'Banner text informing provider that the client and caregiver each need to sign a consent, for different reasons',
  },
  clientFirstNameFallback: {
    defaultMessage: 'your client',
    description: 'Used in place of the client first name in consent banners when the name is not available',
  },
  caregiverFirstNameFallback: {
    defaultMessage: 'the caregiver',
    description: 'Used in place of the caregiver first name in consent banners when the name is not available',
  },
})

type ConsentStatusMessages = Partial<Record<ConsentStatus | ConsentBannerStatus, MessageDescriptor>>

const clientMessages: ConsentStatusMessages = {
  [ConsentStatus.EXPIRING_SOON]: consentBannerMessages.expiringSoon,
  [ConsentStatus.EXPIRED]: consentBannerMessages.expired,
  [ConsentStatus.SUPERSEDED]: consentBannerMessages.superseded,
  [ConsentStatus.PENDING]: consentBannerMessages.pending,
}

const caregiverMessages: ConsentStatusMessages = {
  [ConsentStatus.EXPIRING_SOON]: consentBannerMessages.caregiverExpiringSoon,
  [ConsentStatus.EXPIRED]: consentBannerMessages.caregiverExpired,
  [ConsentStatus.SUPERSEDED]: consentBannerMessages.caregiverSuperseded,
  [ConsentStatus.PENDING]: consentBannerMessages.caregiverPending,
}

const clientAndCaregiverMessages: ConsentStatusMessages = {
  [ConsentStatus.EXPIRING_SOON]: consentBannerMessages.clientAndCaregiverExpiringSoon,
  [ConsentStatus.EXPIRED]: consentBannerMessages.clientAndCaregiverExpired,
  [ConsentStatus.SUPERSEDED]: consentBannerMessages.clientAndCaregiverSuperseded,
  [ConsentStatus.PENDING]: consentBannerMessages.clientAndCaregiverPending,
  // Only meaningful with both recipients: each needs to sign for a different reason
  [MIXED_CONSENT_BANNER_STATUS]: consentBannerMessages.clientAndCaregiverMixed,
}

/**
 * Formats the provider-facing informed consent banner for a consent status, or undefined when that
 * status warrants no banner (e.g. SIGNED). The copy depends on who needs to sign: the client, their
 * caregiver, or both (e.g. a DBT for Teens family session). Names fall back to "your client" /
 * "the caregiver" when unknown.
 */
export const getConsentBannerMessage = (
  intl: IntlShape,
  consentStatus: ConsentStatus | ConsentBannerStatus,
  recipients: ConsentBannerRecipients,
): string | undefined => {
  const { client, caregiver } = recipients
  if (!client && !caregiver) return undefined

  const messages = client && caregiver ? clientAndCaregiverMessages : caregiver ? caregiverMessages : clientMessages
  const messageDescriptor = messages[consentStatus]
  if (!messageDescriptor) return undefined

  const possessivePronoun = getPossessivePronoun(client?.pronouns)
  return intl.formatMessage(messageDescriptor, {
    ...(client
      ? { clientFirstName: client.firstName || intl.formatMessage(consentBannerMessages.clientFirstNameFallback) }
      : {}),
    ...(caregiver
      ? {
          caregiverFirstName:
            caregiver.firstName || intl.formatMessage(consentBannerMessages.caregiverFirstNameFallback),
        }
      : {}),
    ...(client && !caregiver
      ? {
          possessivePronoun,
          capitalPossessivePronoun: possessivePronoun.charAt(0).toUpperCase() + possessivePronoun.slice(1),
        }
      : {}),
  })
}
